import React from 'react'
import { Analytics } from "@vercel/analytics/react"
import { Header, Footer } from "./LandingPage"
import { Calendar, Clock } from "lucide-react"
import SEO from './common/SEO'
import { getBlogPosts } from '../lib/blog-data'
import type { BlogPost } from '../lib/blog-posts'
import { useParams } from 'react-router-dom'

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = React.useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    async function loadPost() {
      if (!slug) return;
      try {
        const posts = await getBlogPosts();
        const foundPost = posts.find(p => p.slug === slug);
        setPost(foundPost || null);
      } catch (error) {
        console.error('Error loading post:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadPost();
  }, [slug]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!post) {
    return <div>Post not found</div>;
  }

  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO 
        path={`/blog/${post.slug}`}
        title={post.meta.title}
        description={post.meta.description}
      />
      <Header />
      <main className="flex-1 pt-24">
        <article className="container max-w-3xl">
          <header className="mb-8">
            <div className="mb-4">
              <span className="text-sm font-medium text-[#1F80FF]">{post.category}</span>
            </div>
            <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl mb-4">
              {post.title}
            </h1>
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {post.date}
              </span>
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {post.readTime}
              </span>
            </div>
          </header>
          
          <div className="mb-8">
            <div className="aspect-[16/9] overflow-hidden rounded-2xl">
              <img 
                src={post.coverImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
            {post.imageCredit && (
              <p className="text-sm text-muted-foreground mt-2 text-center italic">
                {post.imageCredit}
              </p>
            )}
          </div>

          <div 
            className="prose prose-lg max-w-none
              prose-headings:font-bold prose-headings:tracking-tight
              prose-h1:text-4xl prose-h1:mb-8
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
              prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:leading-relaxed prose-p:mb-6
              prose-li:my-2
              prose-strong:font-semibold
              prose-blockquote:border-l-4 prose-blockquote:border-[#1F80FF]
              prose-blockquote:pl-4 prose-blockquote:italic
              prose-a:text-[#1F80FF] prose-a:no-underline hover:prose-a:underline
              prose-img:rounded-xl
              prose-pre:bg-gray-50 prose-pre:p-4 prose-pre:rounded-lg
              prose-code:text-[#1F80FF] prose-code:bg-gray-50 prose-code:px-1 prose-code:rounded
              prose-table:border-collapse
              prose-th:border prose-th:border-gray-200 prose-th:p-2 prose-th:bg-gray-50
              prose-td:border prose-td:border-gray-200 prose-td:p-2"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </article>
      </main>
      <Footer />
      <Analytics />
    </div>
  )
}

export default BlogPostPage 